import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  width: number;
  height: number;
  label: string;
  onCanvas: (canvas: HTMLCanvasElement | null) => void;
  /** Called with the CSS width whenever the box resizes. */
  onResize?: (cssWidth: number) => void;
  children?: ReactNode;
}

/** A canvas that always shows the whole logical area, scaled to fit, with overlays on top. */
export function FitCanvas({ width, height, label, onCanvas, onResize, children }: Props) {
  const outerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const cb = useRef({ onCanvas, onResize });
  cb.current = { onCanvas, onResize };
  const ratio = width / height;

  useLayoutEffect(() => {
    const outer = outerRef.current!;
    const measure = () => {
      const r = outer.getBoundingClientRect();
      const w = Math.floor(Math.max(0, Math.min(r.width, r.height * ratio)));
      setBox((prev) => (prev.w === w ? prev : { w, h: Math.floor(w / ratio) }));
      cb.current.onResize?.(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [ratio]);

  return (
    <div className="arena-outer" ref={outerRef}>
      <div className="arena-box" style={{ width: box.w, height: box.h }}>
        <canvas ref={(c) => cb.current.onCanvas(c)} className="arena-canvas" role="img" aria-label={label} />
        {children}
      </div>
    </div>
  );
}
